import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/PersonAddAlt1';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import apiService from '../../services/apiService';
import AddCustomerDialog from './AddCustomerDialog';

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const headerCellSx = {
  '& .MuiTableCell-head': {
    backgroundColor: '#e3f2fd',
    fontWeight: 700,
    borderBottom: '1px solid #bbdefb'
  }
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedIds, setExpandedIds] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleExpand = (id) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const debouncedSearch = useDebounce(search, 300);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size: rowsPerPage,
        sortBy: 'customerId',
        direction: 'ASC',
      };
      if (debouncedSearch.length >= 3) {
        params.name = debouncedSearch;
      }
      const resp = await apiService.customers.list(params);
      const data = resp.data;
      const content = data?.content || [];
      setCustomers(Array.isArray(content) ? content : []);
      setTotalCount(data?.totalElements ?? 0);
    } catch (e) {
      console.error('Failed to load customers', e);
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openAdd = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setSelectedCustomer(row);
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSaveSuccess = async () => {
    await loadCustomers();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 1.5 }}>
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Customers</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Search customers..."
                size="small"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                fullWidth
              />
              <Button variant="contained" onClick={openAdd} sx={{ minWidth: 'auto', px: 1.5 }}>
                <AddIcon />
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Customers</Typography>
              <Typography variant="caption" color="text.secondary">Search, add, and edit customers</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Search customers"
                size="small"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ width: 250 }}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ textTransform: 'none' }}>Add Customer</Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: '70px' }}>
        {isMobile ? (
          <Box sx={{ pb: 1 }}>
            {customers.map((row) => (
              <Paper key={row.customerId} sx={{ p: 1.5, mb: 1, mx: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {row.customerName}
                      </Typography>
                      {row.customerNameTamil && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.customerNameTamil}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.customerCity}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => openEdit(row)} sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => toggleExpand(row.customerId)} sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {expandedIds[row.customerId] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Mobile</Typography>
                      <Typography variant="body2" fontWeight={600}>{row.customerMobile || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">ID</Typography>
                      <Typography variant="body2" fontWeight={600}>{row.customerId}</Typography>
                    </Box>
                  </Box>

                  <Collapse in={expandedIds[row.customerId]} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Page</Typography>
                        <Typography variant="body2">{row.bookPage || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Shop</Typography>
                        <Typography variant="body2">{row.shopName || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Office No</Typography>
                        <Typography variant="body2">{row.customerOfficeNumber || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">State</Typography>
                        <Typography variant="body2">{row.customerState || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Postal</Typography>
                        <Typography variant="body2">{row.customerPostalCode || '-'}</Typography>
                      </Box>
                    </Box>
                  </Collapse>
              </Paper>
            ))}
            {!loading && customers.length === 0 && (
              <Typography variant="body2" align="center" sx={{ mt: 4, color: 'text.secondary' }}>No customers found</Typography>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader size="small">
            <TableHead sx={headerCellSx}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Page</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Name (Tamil)</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Office No</TableCell>
                <TableCell>Shop</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((row) => (
                <TableRow key={row.customerId} hover>
                  <TableCell>{row.customerId}</TableCell>
                  <TableCell>{row.bookPage}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>{row.customerNameTamil}</TableCell>
                  <TableCell>{row.customerCity}</TableCell>
                  <TableCell>{row.customerMobile}</TableCell>
                  <TableCell>{row.customerState}</TableCell>
                  <TableCell>{row.customerOfficeNumber}</TableCell>
                  <TableCell>{row.shopName}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">No customers found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </Box>

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <AddCustomerDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSaveSuccess={handleSaveSuccess}
        initialData={selectedCustomer}
      />
    </Box>
  );
};

export default Customers;
